import { useCallback, useEffect, useRef, useState } from 'react';
import type { HTMLAttributes, MouseEvent as ReactMouseEvent } from 'react';
import { dragRange, previewSelection } from '@/utils/dragSelection';
import type { DragMode } from '@/utils/dragSelection';

interface Options<K> {
  /** Keys of the rows currently displayed, in display order. */
  keys: K[];
  selected: K[];
  onChange: (next: K[]) => void;
}

interface Drag {
  anchor: number;
  current: number;
  mode: DragMode;
  /** The cursor left the anchor row at least once — it is a drag, not a click. */
  moved: boolean;
}

/** The cell antd renders the row checkbox into. */
const SELECTION_CELL = '.ant-table-selection-column';

/**
 * Press a row checkbox and drag up or down: every row between the press and
 * the cursor is previewed as selected (or deselected, when the press landed
 * on a selected row) and committed on release. A plain click still toggles
 * one row through antd.
 *
 * Returns the `selectedRowKeys` to render (committed plus the live preview)
 * and an `onRow` to merge into the table props.
 */
export function useDragRowSelection<K>({
  keys,
  selected,
  onChange,
}: Options<K>) {
  const [drag, setDrag] = useState<Drag | null>(null);
  // Handlers registered on `window` must see the latest state without
  // re-subscribing on every move.
  const latest = useRef({ keys, selected, onChange });
  useEffect(() => {
    latest.current = { keys, selected, onChange };
  });
  // Set on release after a real drag; the click that follows the release is
  // antd toggling the anchor checkbox, which must not happen a second time.
  const suppressClick = useRef(false);

  const preview = drag
    ? previewSelection(
        selected,
        dragRange(keys, drag.anchor, drag.current),
        drag.mode
      )
    : selected;

  useEffect(() => {
    if (!drag) return;
    const finish = (commit: boolean) => {
      const { keys, selected, onChange } = latest.current;
      if (commit && drag.moved) {
        onChange(
          previewSelection(
            selected,
            dragRange(keys, drag.anchor, drag.current),
            drag.mode
          )
        );
        suppressClick.current = true;
        // No click follows a release outside the table — don't swallow the next one.
        window.setTimeout(() => (suppressClick.current = false), 0);
      }
      setDrag(null);
    };
    const onUp = () => finish(true);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finish(false);
    };
    window.addEventListener('mouseup', onUp);
    window.addEventListener('keydown', onKey);
    // Dragging across cells would otherwise highlight their text.
    const prev = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    return () => {
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('keydown', onKey);
      document.body.style.userSelect = prev;
    };
  }, [drag]);

  const onRow = useCallback(
    (_record: unknown, index?: number): HTMLAttributes<HTMLElement> => ({
      onMouseDown: (e: ReactMouseEvent) => {
        if (index == null || e.button !== 0) return;
        const el = e.target;
        if (!(el instanceof Element) || !el.closest(SELECTION_CELL)) return;
        const key = latest.current.keys[index];
        if (key === undefined) return;
        setDrag({
          anchor: index,
          current: index,
          mode: latest.current.selected.includes(key) ? 'deselect' : 'select',
          moved: false,
        });
      },
      onMouseEnter: () => {
        if (index == null) return;
        setDrag((d) =>
          d && d.current !== index ? { ...d, current: index, moved: true } : d
        );
      },
      // After a drag the release also fires antd's checkbox click on the
      // anchor row; that would toggle it a second time.
      onClickCapture: (e: ReactMouseEvent) => {
        if (suppressClick.current) {
          suppressClick.current = false;
          e.stopPropagation();
          e.preventDefault();
        }
      },
    }),
    []
  );

  return { selectedRowKeys: preview, onRow, dragging: drag != null };
}
